import React, { useState, useEffect, useCallback, useMemo } from "react";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";

import { Table } from "components";
import { useNoteModal } from "components/tables/notes";
import { getErrorMessage, hasPermissions, viewAppPermissionSet } from "helpers";
import { useTableProps } from "hooks";
import { useCloneMpaMutation, useCopyMpaDocumentMutation } from "queries";
import { applicationDealNotesService, applicationListService } from "services";
import { uiActions } from "store";
import { getFilters } from "store/filters/filters.reducer";
import { ErrorResponse } from "types";

import ApplicationListContext from "./applicationListContext";
import useApplicationList from "./useApplicatonList";

const ApplicationsTable = ({ getList, columns, form }) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const filters = useSelector(getFilters(form));

	const [fetchData, setFetchData] = useState({
		sortBy: { id: "", desc: false },
		page: 0,
		filters,
	});

	useEffect(() => {
		if (filters !== fetchData.filters)
			setFetchData({ ...fetchData, page: 0, filters: filters });
	}, [fetchData, filters]);

	const queryResult = useApplicationList({
		getList,
		fetchData,
	});

	const { meta, data, refetch } = useTableProps(queryResult);

	const initialState = useMemo(
		() => ({
			sortBy: [fetchData.sortBy],
			pageIndex: fetchData.page,
		}),
		[fetchData],
	);

	const handlePageChange = useCallback(
		({ pageIndex }) => {
			if (pageIndex !== fetchData.page)
				setFetchData({ ...fetchData, page: pageIndex });
		},
		[fetchData],
	);

	const handleSortChange = useCallback(
		({ sortBy }) => {
			setFetchData({ ...fetchData, sortBy: sortBy[0] });
		},
		[fetchData],
	);

	const { showAddNoteModal } = useNoteModal({
		key: "applicationList",
		service: applicationDealNotesService,
	});

	const removeApplication = useCallback(
		(id, number) => {
			dispatch(
				uiActions.showModal("CONFIRMATION", {
					title: `Are you sure you want to remove application ${number}?`,
					buttonCancel: "Cancel",
					buttonOk: "Remove",
					onConfirm() {
						applicationListService
							.removeApplication(id)
							.then(() => {
								dispatch(
									uiActions.showSuccessNotification({
										title: "Application has been removed successfully",
									}),
								);
								refetch?.({ throwOnError: true });
							})
							.catch(error => {
								dispatch(
									uiActions.showErrorNotification({
										title: getErrorMessage(error),
									}),
								);
							});
					},
				}),
			);
		},
		[dispatch, refetch],
	);

	const { mutateAsync: copyMpaDocuments } = useCopyMpaDocumentMutation();

	const { mutateAsync: cloneMpa } = useCloneMpaMutation();

	const handleCloneMpa = useCallback(
		async (appId, selectedDocumentsId) => {
			const successHandler = (appId, message) => {
				history.push(`/applications/upload-mpa/${appId}/general-info`);
				dispatch(
					uiActions.showSuccessNotification({
						title: message,
					}),
				);
			};

			dispatch(uiActions.showSpinnerOverlay());

			const newAppId = await cloneMpa({ appId });

			if (newAppId && selectedDocumentsId?.length) {
				const copyingRequests = selectedDocumentsId?.map(documentId =>
					copyMpaDocuments({
						appId: newAppId,
						documentId,
					}),
				);

				Promise.all(copyingRequests).then(
					() => {
						dispatch(uiActions.hideSpinnerOverlay());

						successHandler(
							newAppId,
							"Attachments copying succeeded",
						);
					},
					(error: ErrorResponse) => {
						dispatch(
							uiActions.showErrorNotification({
								title: getErrorMessage(error),
							}),
						);
					},
				);
			} else if (newAppId) {
				dispatch(uiActions.hideSpinnerOverlay());
				successHandler(newAppId, "Application copying succeeded");
			}
		},
		[cloneMpa, copyMpaDocuments, dispatch, history],
	);

	const saveDocumentsCopy = useCallback(
		(appId, ids, applicationDocuments) => {
			const documentsToCopy = _.filter(applicationDocuments, v =>
				_.includes(ids, v.id),
			);

			dispatch(uiActions.showSpinnerOverlay());

			applicationListService
				.saveApplicationDocumentsCopy(appId, documentsToCopy)
				.then(applicationId => {
					dispatch(uiActions.hideSpinnerOverlay());
					history.push("/applications/edit/" + applicationId);
					dispatch(
						uiActions.showSuccessNotification({
							title: "Attachments copying succeeded",
						}),
					);
				})
				.catch(_error => {
					dispatch(uiActions.hideSpinnerOverlay());
					dispatch(
						uiActions.showErrorNotification({
							title: "Couldn't add a attachments copying",
						}),
					);
				});
		},
		[history, dispatch],
	);

	const copyDocument = useCallback(
		(applicationId, isPaperApplication) => {
			const saveDocumentsCopyFn = (
				selectedDocumentsId,
				applicationDocuments,
			) => {
				isPaperApplication
					? handleCloneMpa(applicationId, selectedDocumentsId)
					: saveDocumentsCopy(
							applicationId,
							selectedDocumentsId,
							applicationDocuments,
					  );
			};

			dispatch(
				uiActions.showModal("APP_LIST_DOCUMENTS", {
					applicationId,
					isPaperApplication,
					saveDocumentsCopy: saveDocumentsCopyFn,
				}),
			);
		},
		[dispatch, handleCloneMpa, saveDocumentsCopy],
	);

	const handleOpenApplication = useCallback(
		row =>
			hasPermissions(viewAppPermissionSet) &&
			history.push(`/applications/${row?.original?.id}`),
		[history],
	);
	const handlerAssignToUser = useCallback(
		({ values, row }) => {
			applicationListService
				.assignToUser?.({
					applicationId: row.original?.id,
					assigneeId: values.assignTo?.id,
				})
				.then(() => refetch?.({ throwOnError: true }))
				.catch(error => {
					dispatch(uiActions.showErrorNotification({ title: error }));
				});
		},
		[dispatch, refetch],
	);

	return (
		<ApplicationListContext.Provider
			value={{
				refetch,
				copyDocument,
				removeApplication,
				showAddNoteModal,
			}}
		>
			<Table
				columns={columns}
				meta={meta}
				data={data}
				onRowClick={handleOpenApplication}
				handlePageChange={handlePageChange}
				handleSortChange={handleSortChange}
				handleRowSubmit={handlerAssignToUser}
				initialState={initialState}
				stickyFooter
				stickyHeader
				compact
			/>
		</ApplicationListContext.Provider>
	);
};

export default ApplicationsTable;
